// Pieza de `onSaleChanged`: el wizard avisa que cambiaron extras o forma de pago y se reaplica (REQ-13).
import type { QueryClient } from '@tanstack/react-query';
import { WIZARD_KEYS } from '@/domains/sales/wizard/api/useWizard';
import { anchorLineOf, extraLinesOf } from '@/domains/sales/wizard/buildSale';
import {
    anchorGiftLinesOf,
    SaleChangeBlockedError,
    type OnSaleChanged,
} from '@/domains/sales/wizard/promotionSlots';
import type { WizardSession } from '@/domains/sales/wizard/types';
import { cachedEvaluation, evaluationKey } from '../api/useWizardPromotions';
import { wizardPromotionsClient } from '../api/wizardPromotions.client';
import { withCustomerPhone } from '../lib/customerPhone';
import { reapplySelection, type AppliedSelection, type PromotionsApi } from '../lib/applySelection';
import {
    paymentChangeFailedCopy,
    paymentDroppedNoticeCopy,
    sureKeepReleaseFailedCopy,
} from '../lib/promotionErrorCopy';
import { chosenPromotionIds } from '../lib/promotionSelection';
import { rememberedPromotionName, rememberedSureKeepOnly } from '../lib/promotionNames';
import type { PromotionSelection } from '../lib/promotionSelection';
import {
    isNothingReleased,
    mergeReleased,
    NOTHING_RELEASED,
    releasedBetween,
    releasedFor,
    sureKeepOnlyHeld,
    withReleased,
    type SureKeepReleased,
} from '../lib/sureKeepRelease';
import { notifyDroppedPromotions } from './droppedPromotionNotice';
import {
    bumpLinesVersion,
    codeIsSureKeepOnly,
    recordDiscountCodeBlock,
    seedSessionPromotions,
    sessionPromotionState,
    storePort,
    usePromotionSelectionStore,
} from '../store/promotionSelectionStore';

/** Fuera de «Arma la venta» (recarga en Forma de pago) la selección sale de la sesión en caché. */
export function seedFromCachedSession(sessionId: string, queryClient?: QueryClient): void {
    seedFromWizardSession(
        sessionId,
        queryClient?.getQueryData<WizardSession>(WIZARD_KEYS.session(sessionId)),
    );
}

/** Siembra la selección con lo apartado en esa sesión, si aún no se conoce. */
export function seedFromWizardSession(sessionId: string, session: WizardSession | undefined): void {
    const anchor = session && anchorLineOf(session);
    if (!session || !anchor) return;
    seedSessionPromotions(sessionId, {
        anchorLine: anchor,
        salePromotion: session.salePromotion,
        ...(session.salePromotions && { salePromotions: session.salePromotions }),
        anchorGiftLines: anchorGiftLinesOf(session),
        extraLines: extraLinesOf(session),
    });
}

/** La forma de pago de la venta en caché es crédito SureKeep (o aún no se elige). */
function paysWithSureKeep(sessionId: string, queryClient?: QueryClient): boolean {
    const session = queryClient?.getQueryData<WizardSession>(WIZARD_KEYS.session(sessionId));
    const kind = session?.paymentMethodKind ?? null;
    return kind === null || kind === 'SUREKEEP';
}

export interface OnSaleChangedDeps {
    sessionId: string;
    queryClient?: QueryClient;
    api?: PromotionsApi;
}

export function createPromotionsOnSaleChanged({
    sessionId,
    queryClient,
    api = wizardPromotionsClient,
}: OnSaleChangedDeps): OnSaleChanged {
    const { patch } = usePromotionSelectionStore.getState();
    const isSureKeepOnly = (id: string) => rememberedSureKeepOnly(sessionId, id);
    const isSureKeepOnlyCode = (code: string) => codeIsSureKeepOnly(sessionId, code);
    return (change) => {
        bumpLinesVersion(sessionId);
        seedFromCachedSession(sessionId, queryClient);
        const session = queryClient?.getQueryData<WizardSession>(WIZARD_KEYS.session(sessionId));
        const isPaymentChange = change.kind === 'paymentMethod';
        const withoutSureKeep =
            isPaymentChange && !(change.sureKeep ?? paysWithSureKeep(sessionId, queryClient));
        const current = storePort(sessionId).read();
        const held = current
            ? sureKeepOnlyHeld(current, isSureKeepOnly, isSureKeepOnlyCode)
            : { promotionIds: [], code: null };
        const anchorLineId = (session && anchorLineOf(session)?.lineId) ?? current?.anchorLineId;
        const stored = sessionPromotionState(sessionId).sureKeepReleased;
        const released = releasedFor(stored, anchorLineId);
        // Lo soltado con otro teléfono ya no se reaparta ni cuenta en Pago.
        if (released !== stored && !isNothingReleased(stored)) {
            patch(sessionId, { sureKeepReleased: NOTHING_RELEASED });
        }
        const restoring = isPaymentChange && !withoutSureKeep && !isNothingReleased(released);
        const codeCents = evaluatedCodeCents(sessionId, held.code, queryClient);
        const liveLineIds = (session?.reservedLines ?? [])
            .filter((l) => l.status !== 'RELEASING')
            .map((l) => l.lineId);
        let before: PromotionSelection | null = null;
        const extend = (base: PromotionSelection): PromotionSelection => {
            before = base;
            return restoring ? withReleased(base, released, liveLineIds) : base;
        };
        const nameOf = (id: string) => rememberedPromotionName(sessionId, id) ?? 'una promo';
        const heldNames = [
            ...held.promotionIds.map(nameOf),
            ...(held.code ? [`el código ${held.code}`] : []),
        ];
        const chosenNames = current ? chosenPromotionIds(current).map(nameOf) : [];
        let notices: string[] = [];
        const done = reapplySelection(
            sessionId,
            storePort(sessionId),
            withCustomerPhone(api, sessionId, queryClient),
            extend,
        )
            .then((applied) => {
                patch(sessionId, { reapplyError: null });
                if (withoutSureKeep && applied && before) {
                    rememberReleased(sessionId, before, applied, held, session, codeCents);
                }
                // Sin selección conocida no se intentó reapartar: lo memorizado se queda.
                if (restoring && before) patch(sessionId, { sureKeepReleased: NOTHING_RELEASED });
                notifyDroppedPromotions(sessionId, applied, {
                    withoutSureKeep,
                    ...(restoring && { restored: released }),
                    promotionsAsNotices: isPaymentChange,
                });
                if (isPaymentChange) {
                    notices = paymentDropNotices(sessionId, applied, released, !withoutSureKeep);
                }
                if (applied) {
                    void queryClient?.invalidateQueries({
                        queryKey: WIZARD_KEYS.session(sessionId),
                    });
                }
            })
            .catch((error: unknown) => {
                patch(sessionId, { reapplyError: error });
                recordDiscountCodeBlock(sessionId, error);
                throw error;
            })
            .finally(() => {
                void queryClient?.invalidateQueries({ queryKey: evaluationKey(sessionId) });
            });
        if (isPaymentChange) {
            // El paso espera a la reevaluación: si Promociones falla no avanza, para no cobrar con una promo que no aplica.
            return done.then(
                () => ({ notices }),
                (error: unknown) => {
                    throw new SaleChangeBlockedError(
                        withoutSureKeep
                            ? sureKeepReleaseFailedCopy(heldNames, error)
                            : paymentChangeFailedCopy(chosenNames, error),
                    );
                },
            );
        }
        done.catch(() => undefined);
        return undefined;
    };
}

/** Lo que baja el código según la evaluación en caché, si aplica. */
function evaluatedCodeCents(
    sessionId: string,
    code: string | null,
    queryClient?: QueryClient,
): number | undefined {
    const evaluated = cachedEvaluation(sessionId, queryClient)?.discountCode;
    return code && evaluated?.applicable && evaluated.codeText === code
        ? evaluated.totalDiscountCents
        : undefined;
}

/** Guarda lo solo SureKeep que se soltó y los códigos que siguieron aplicando de contado. */
function rememberReleased(
    sessionId: string,
    before: PromotionSelection,
    applied: AppliedSelection,
    held: { promotionIds: string[]; code: string | null },
    session: WizardSession | undefined,
    codeCents: number | undefined,
): void {
    const state = sessionPromotionState(sessionId);
    const notApplicable = applied.droppedCode?.rejectionCode === 'CODE_NOT_APPLICABLE';
    const released = releasedBetween(
        before,
        applied.selection,
        { promotionIds: held.promotionIds, code: notApplicable ? held.code : null },
        session,
        codeCents,
    );
    const keptCode = before.code && applied.selection.code === before.code ? before.code : null;
    usePromotionSelectionStore.getState().patch(sessionId, {
        sureKeepReleased: mergeReleased(state.sureKeepReleased ?? NOTHING_RELEASED, released),
        ...(keptCode && {
            anyFinancierCodes: [...new Set([...(state.anyFinancierCodes ?? []), keptCode])],
        }),
    });
}

/** Avisos de lo que soltó un cambio de forma de pago; lo que se intentó regresar ya avisa por toast. */
function paymentDropNotices(
    sessionId: string,
    applied: AppliedSelection | null,
    restored: SureKeepReleased,
    sureKeep: boolean,
): string[] {
    const restoredIds = new Set(restored.choices.map((c) => c.choice.promotionId));
    const dropped = [
        ...(applied?.droppedPromotionIds ?? []),
        ...(applied?.blockedDrops ?? []).map((drop) => drop.promotionId),
    ].filter((id) => !restoredIds.has(id));
    return [...new Set(dropped)].map((id) =>
        paymentDroppedNoticeCopy(rememberedPromotionName(sessionId, id), sureKeep),
    );
}
